/**
 * Users Redux Slice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  User,
  UserStats,
  UsersFilter,
  PaginationMeta,
  UsersListResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ChangeUserRolePayload,
  BlockUserPayload,
  UnlockAttemptsPayload,
  ResetPasswordPayload,
} from '../domain/types'

export interface UsersState {
  items: User[]
  selectedUser: User | null
  stats: UserStats
  filter: UsersFilter
  pagination: PaginationMeta
  loading: boolean
  error: string | null
  createUserStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  createUserError: string | null
  updateUserStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  updateUserError: string | null
}

const initialState: UsersState = {
  items: [],
  selectedUser: null,
  stats: {
    active: 0,
    inactive: 0,
    invited: 0,
    blocked: 0,
    total: 0,
    byRole: {},
  },
  filter: {
    status: 'all',
    role: 'all',
    userType: 'all',
    lastLogin: 'all',
    tenantId: 'all',
    page: 1,
    pageSize: 10,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    pageCount: 1,
    total: 0,
  },
  loading: false,
  error: null,
  createUserStatus: 'idle',
  createUserError: null,
  updateUserStatus: 'idle',
  updateUserError: null,
}

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    // Fetch actions
    fetchUsers: (state, _action: PayloadAction<UsersFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchUsersSuccess: (state, action: PayloadAction<UsersListResponse>) => {
      state.items = action.payload.data
      state.pagination = action.payload.meta
      if (action.payload.globalStats) {
        state.stats = action.payload.globalStats
      }
      state.loading = false
      state.error = null
    },
    fetchUsersFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload
      state.filter.page = 1 // Reset to first page when changing page size
    },

    // Legacy setters
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload
    },
    selectUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload
    },
    setStats: (state, action: PayloadAction<UserStats>) => {
      state.stats = action.payload
    },
    setFilter: (state, action: PayloadAction<Partial<UsersFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Create user
    createUser: (state, _action: PayloadAction<CreateUserRequest>) => {
      state.loading = true
      state.error = null
      state.createUserStatus = 'pending'
      state.createUserError = null
    },
    createUserSuccess: (state, action: PayloadAction<User>) => {
      state.items.push(action.payload)
      state.stats.active += 1
      state.stats.total += 1
      state.loading = false
      state.error = null
      state.createUserStatus = 'succeeded'
      state.createUserError = null
    },
    createUserFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
      state.createUserStatus = 'failed'
      state.createUserError = action.payload
    },
    resetCreateUserState: (state) => {
      state.createUserStatus = 'idle'
      state.createUserError = null
    },

    // Update user
    updateUser: (state, _action: PayloadAction<UpdateUserRequest>) => {
      state.loading = true
      state.error = null
      state.updateUserStatus = 'pending'
      state.updateUserError = null
    },
    updateUserSuccess: (state, action: PayloadAction<User>) => {
      const index = state.items.findIndex((u) => u.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedUser?.id === action.payload.id) {
        state.selectedUser = action.payload
      }
      state.loading = false
      state.error = null
      state.updateUserStatus = 'succeeded'
      state.updateUserError = null
    },
    updateUserFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
      state.updateUserStatus = 'failed'
      state.updateUserError = action.payload
    },
    resetUpdateUserState: (state) => {
      state.updateUserStatus = 'idle'
      state.updateUserError = null
    },

    // Change role (admin action)
    changeUserRole: (state, _action: PayloadAction<ChangeUserRolePayload>) => {
      state.loading = true
    },
    changeUserRoleSuccess: (state, action: PayloadAction<User>) => {
      const index = state.items.findIndex((u) => u.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedUser?.id === action.payload.id) {
        state.selectedUser = action.payload
      }
      state.loading = false
    },
    changeUserRoleFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Block/unblock user (admin action)
    blockUser: (state, _action: PayloadAction<BlockUserPayload>) => {
      state.loading = true
    },
    blockUserSuccess: (state, action: PayloadAction<User>) => {
      const index = state.items.findIndex((u) => u.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
        // Update stats
        if (action.payload.blocked) {
          state.stats.blocked += 1
          state.stats.active -= 1
        } else {
          state.stats.blocked -= 1
          state.stats.active += 1
        }
      }
      if (state.selectedUser?.id === action.payload.id) {
        state.selectedUser = action.payload
      }
      state.loading = false
    },
    blockUserFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Unlock user locked by too many failed attempts
    unlockAttempts: (state, _action: PayloadAction<UnlockAttemptsPayload>) => {
      state.loading = true
    },
    unlockAttemptsSuccess: (state, action: PayloadAction<User>) => {
      const index = state.items.findIndex((u) => u.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedUser?.id === action.payload.id) {
        state.selectedUser = action.payload
      }
      state.loading = false
    },
    unlockAttemptsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Reset password (admin action)
    resetUserPassword: (state, _action: PayloadAction<ResetPasswordPayload>) => {
      state.loading = true
    },
    resetUserPasswordSuccess: (state) => {
      state.loading = false
    },
    resetUserPasswordFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Filter actions
    searchUsers: (state, action: PayloadAction<string>) => {
      state.filter = { ...state.filter, search: action.payload }
    },
    filterByStatus: (state, action: PayloadAction<UsersFilter['status']>) => {
      state.filter = { ...state.filter, status: action.payload }
    },
    filterByRole: (state, action: PayloadAction<UsersFilter['role']>) => {
      state.filter = { ...state.filter, role: action.payload }
    },
    filterByTenant: (state, action: PayloadAction<UsersFilter['tenantId']>) => {
      state.filter = { ...state.filter, tenantId: action.payload }
    },

    // Clear
    clearUsers: (state) => {
      state.items = []
      state.selectedUser = null
      state.error = null
      state.createUserStatus = 'idle'
      state.createUserError = null
      state.updateUserStatus = 'idle'
      state.updateUserError = null
    },
  },
})

export const usersActions = usersSlice.actions
export const usersReducer = usersSlice.reducer
